import { createContext, memo, type ReactNode, useContext, useEffect, useRef } from 'react';
import { usePrevious } from 'react-use';
import invariant from 'tiny-invariant';
import { createStore, type StoreApi, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type AssetModalPanelSlice,
  AssetModalPanelState,
  createAssetModalPanelSlice,
} from '~/components/v2/store/features/asset-modal/slices/createAssetModalPanelSlice';
import {
  AssetModalViewMode,
  type AssetModalViewModeSlice,
  createAssetModalViewModeSlice,
} from '~/components/v2/store/features/asset-modal/slices/createAssetModalViewModeSlice';
import { createThemeSlice, Theme, type ThemeSlice } from '~/components/v2/store/shared/createThemeSlice';
import { type ZustandStorageKeys } from '~/constants/zustandStorageKeys';

export type AssetModalStoreState = AssetModalViewModeSlice & AssetModalPanelSlice & ThemeSlice;

export const AssetModalStoreContext = createContext<StoreApi<AssetModalStoreState> | null>(null);

export type AssetModalStoreContextProviderProps = {
  children: ReactNode;
  id: string;
  storageKey: ZustandStorageKeys;
};

export const AssetModalStoreContextProvider = memo(
  ({ children, id, storageKey }: AssetModalStoreContextProviderProps) => {
    const storeRef = useRef<StoreApi<AssetModalStoreState> | null>(null);
    const previousIdRef = usePrevious(id);

    invariant(storageKey, 'storageKey is required');

    if (!storeRef.current) {
      storeRef.current = createStore<AssetModalStoreState>()(
        persist(
          (...args) => ({
            ...createAssetModalViewModeSlice({
              viewMode: AssetModalViewMode.View,
            })(...args),
            ...createAssetModalPanelSlice({
              bottomPanel: AssetModalPanelState.CLOSED,
              leftPanel: AssetModalPanelState.CLOSED,
              rightPanel: AssetModalPanelState.CLOSED,
            })(...args),
            ...createThemeSlice({ theme: Theme.Dark })(...args),
          }),
          {
            name: storageKey,
            partialize: (state) => ({
              theme: state.theme,
            }),
            version: 1,
          },
        ),
      );
    }

    useEffect(() => {
      const state = storeRef.current?.getState();

      const hasChanged = previousIdRef !== id;
      const isEditViewMode = state?.viewMode === AssetModalViewMode.EditVideo;

      if (id && state && hasChanged && isEditViewMode) {
        state.resetViewMode();
        state.resetBottomPanel();
      }
    }, [id, previousIdRef]);

    return <AssetModalStoreContext.Provider value={storeRef.current}>{children}</AssetModalStoreContext.Provider>;
  },
);

AssetModalStoreContextProvider.displayName = 'AssetModalStoreContextProvider';

export const useAssetModalStore = <T,>(selector: (store: AssetModalStoreState) => T): T => {
  const store = useContext(AssetModalStoreContext);

  if (!store) {
    const context = 'useAssetModalStore must be used within an AssetModalStoreContextProvider';
    const error = new Error(context);

    reportErrorToBugsnag({
      error,
      context,
    });

    throw error;
  }

  return useStore(store, selector);
};
